/**
 * Work session history.
 *
 * The floor shows what is running and the dashboard shows the last few things to finish. Once a
 * session scrolled off that short list there was no way to find it again — the detail page existed
 * and nothing linked to it. Reports answer "what happened in aggregate"; this answers "show me that
 * session", which is a different question and the one a manager asks when something looks wrong.
 *
 * The developer filter is here deliberately and the breakdowns in Reports deliberately are not.
 * Filtering to one person's sessions is how somebody reviews their own work, or how a manager
 * follows up on a particular piece of it. Ranking people against each other is a different feature
 * that §43 says not to build, and listing sessions is not a step towards it: there is no score, no
 * ordering by output, and no column that invites comparison.
 */

import { HistoryLimitNote } from '../components/HistoryLimitNote'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatDate, formatDuration } from '../lib/format'
import { Avatar, Badge, EmptyState, Panel, ProgressBar, Skeleton, toneForState } from '../components/primitives'

const PAGE_SIZE = 25

const STATUSES = [
  'Started', 'Planning', 'Analyzing', 'Implementing', 'Testing',
  'Reviewing', 'Blocked', 'Completed', 'Failed', 'Cancelled',
]

export default function Sessions() {
  const { t, locale } = useI18n()

  const [developerId, setDeveloperId] = useState('')
  const [status, setStatus] = useState('')
  const [includeSimulated, setIncludeSimulated] = useState(true)
  const [page, setPage] = useState(1)

  const { data: developers } = useQuery({ queryKey: ['developers'], queryFn: api.settings.developers })

  const { data, isLoading } = useQuery({
    queryKey: ['sessions', { developerId, status, includeSimulated, page }],
    queryFn: () =>
      api.sessions.list({
        page,
        pageSize: PAGE_SIZE,
        developerId: developerId || undefined,
        status: status || undefined,
        includeSimulated,
      }),
    // Keeps the table on screen while the next page loads, instead of collapsing to a skeleton and
    // shifting everything the reader was looking at.
    placeholderData: keepPreviousData,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const reset = (apply: () => void) => {
    apply()
    setPage(1)
  }

  return (
    <div className="stack">
      <HistoryLimitNote />
      <div className="row row--wrap">
        <label className="field" style={{ minWidth: 200 }}>
          <span className="field__label">{t('work.developer')}</span>
          <select
            className="select"
            value={developerId}
            onChange={(event) => reset(() => setDeveloperId(event.target.value))}
          >
            <option value="">{t('common.all')}</option>
            {(developers ?? []).map((developer) => (
              <option key={developer.id} value={developer.id}>{developer.displayName}</option>
            ))}
          </select>
        </label>

        <label className="field" style={{ minWidth: 180 }}>
          <span className="field__label">{t('tasks.status')}</span>
          <select className="select" value={status} onChange={(event) => reset(() => setStatus(event.target.value))}>
            <option value="">{t('common.all')}</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>{t(`sessionStatus.${value}`)}</option>
            ))}
          </select>
        </label>

        <label className="row row--tight" style={{ alignSelf: 'flex-end', paddingBottom: 6 }}>
          <input
            type="checkbox"
            checked={includeSimulated}
            onChange={(event) => reset(() => setIncludeSimulated(event.target.checked))}
          />
          <span className="small">{t('reports.includeSimulated')}</span>
        </label>

        <div className="spacer" />

        <span className="tiny muted" style={{ alignSelf: 'flex-end', paddingBottom: 8 }}>
          {t('common.showing', { count: items.length, total })}
        </span>
      </div>

      <Panel flush>
        {isLoading && !data ? (
          <div className="stack" style={{ padding: 'var(--s4)' }}>
            <Skeleton height={36} />
            <Skeleton height={36} />
            <Skeleton height={36} />
          </div>
        ) : items.length === 0 ? (
          <EmptyState title={t('live.empty')} body={t('live.emptyHint')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('work.developer')}</th>
                <th>{t('tasks.externalId')}</th>
                <th>{t('tasks.status')}</th>
                <th>{t('work.progress')}</th>
                <th>{t('live.duration')}</th>
                <th>{t('work.model')}</th>
                <th>{t('tasks.created')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((session) => (
                <tr key={session.workSessionId}>
                  <td>
                    <Link to={`/work/${session.workSessionId}`} className="row row--tight" style={{ color: 'inherit', textDecoration: 'none' }}>
                      <Avatar name={session.developerName} color={session.avatarColor} size="sm" />
                      <span className="small bold truncate">{session.developerName}</span>
                    </Link>
                  </td>
                  <td className="mono small nowrap">{session.taskExternalId ?? '—'}</td>
                  <td>
                    <Badge tone={toneForState(session.activityState)}>{t(`state.${session.activityState}`)}</Badge>
                  </td>
                  <td style={{ width: 120 }}>
                    <ProgressBar value={session.progressPercent} showValue size="sm" />
                  </td>
                  <td className="tiny muted nowrap">{formatDuration(session.durationSeconds, locale)}</td>
                  <td className="tiny muted">{session.modelName ?? '—'}</td>
                  <td className="tiny muted nowrap">{formatDate(session.startedAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {lastPage > 1 && (
        <div className="row row--tight" style={{ justifyContent: 'center' }}>
          <button
            type="button"
            className="btn btn--sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            <ChevronLeft size={14} />
          </button>

          <span className="tiny muted">{t('common.page')} {page} / {lastPage}</span>

          <button
            type="button"
            className="btn btn--sm"
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
            disabled={page >= lastPage}
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  )
}
